import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { errMsg } from "./ResolveDialog";

const RESULT = { approved: "approved in the Resource Library", already_approved: "was already approved",
  needs_approver: "still needs a clinical approver in the Resource Library", failed: "couldn't be approved" };

const waiting = (i) => i.status !== "approved" || !!i.pending || i.type === "printable_diary";

function LibraryItems({ items, canApprove, checked, onChecked }) {
  const pending = items.filter(waiting);
  return (
    <div className="space-y-2 rounded-lg border border-zinc-200 bg-zinc-50 p-3" data-testid="publish-library">
      <div className="text-sm font-semibold text-zinc-950">Linked Resource Library item{items.length > 1 ? "s" : ""}</div>
      <ul className="space-y-1 text-sm">
        {items.map((i) => (
          <li key={i.id} data-testid={`publish-library-item-${i.id}`}>
            <div className="flex items-center gap-2">
              <span className="flex-1">{i.title}</span>
              <span className={`rounded px-1.5 py-0.5 text-2xs font-semibold ${i.status === "approved" ? "bg-emerald-100 text-emerald-900" : "bg-amber-100 text-amber-900"}`}>{i.status.replace("_", " ")}</span>
            </div>
            {i.type === "printable_diary" && <p className="text-2xs text-zinc-500" data-testid={`publish-library-rebuild-${i.id}`}>A new printable draft is built from this version (name, questions and scales).</p>}
          </li>
        ))}
      </ul>
      {pending.length > 0 && (canApprove ? (
        <label className="flex items-start gap-2 text-sm">
          <Checkbox checked={checked} onCheckedChange={(v) => onChecked(!!v)} className="mt-0.5" data-testid="publish-approve-library" />
          <span>Also approve {pending.length === 1 ? "it" : "them"} in the Resource Library (including the rebuilt printable), so {pending.length === 1 ? "it" : "they"} can be sent from the library</span>
        </label>
      ) : <p className="text-xs text-amber-800" data-testid="publish-library-needs-approver">Only a clinical approver can approve library items — they&apos;ll stay waiting for one.</p>)}
    </div>
  );
}

/** Approve & publish a template version, optionally approving its linked library items in the same step. */
export function PublishDialog({ v, onClose, onDone }) {
  const items = v.library_items || [];
  const [note, setNote] = useState("");
  const [lib, setLib] = useState(v.can_approve_library && items.some((i) => i.status !== "approved" || !!i.pending));
  const [busy, setBusy] = useState(false);
  async function publish() {
    setBusy(true);
    try {
      const { data } = await api.post(`/checkin-templates/versions/${v.id}/status`, { status: "published", note: note.trim(), approve_library: lib });
      toast.success(`Published v${data.semantic_version}`, { description: "New enrolments and invitations use this version." });
      (data.library || []).forEach((i) => (i.result === "failed" ? toast.error : toast.success)(`${i.title} ${RESULT[i.result]}`, i.reason ? { description: i.reason } : undefined));
      onDone();
    } catch (e) {
      toast.error("Couldn't publish", { description: errMsg(e) });
      setBusy(false);
    }
  }
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="publish-dialog">
        <DialogHeader>
          <DialogTitle>Approve & publish v{v.semantic_version}</DialogTitle>
          <DialogDescription>Clients can be sent this version straight away. Published versions can&apos;t be edited — later changes become a new version.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Clinical approval note (who approved, evidence reviewed)</Label>
            <Textarea rows={3} className="mt-1" value={note} onChange={(e) => setNote(e.target.value)} data-testid="publish-note" /></div>
          {items.length > 0 && <LibraryItems items={items} canApprove={v.can_approve_library} checked={lib} onChecked={setLib} />}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="publish-cancel">Cancel</Button>
          <Button onClick={publish} disabled={busy || note.trim().length < 3} data-testid="publish-confirm">
            {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Publish
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
